import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type UserRole = "user" | "agent" | "admin";

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  emirate: string | null;
  country: string | null;
  postal_code: string | null;
  avatar_url: string | null;
  is_online: boolean;
  marketing_opt_in: boolean;
  push_notifications_enabled: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: UserRole[];
  loading: boolean;
  isAgent: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; roles?: UserRole[] }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(false);
  const lastOneSignalUserId = useRef<string | null>(null);

  // Sync user with OneSignal via deferred pattern (guaranteed after SDK init)
  useEffect(() => {
    if (user?.id && user.id !== lastOneSignalUserId.current) {
      lastOneSignalUserId.current = user.id;
      window.OneSignalDeferred?.push(async (OneSignal) => {
        try {
          console.log('[OneSignal] Syncing user:', user.id);
          await OneSignal.login(user.id);
        } catch (error) {
          console.error('[OneSignal] User sync error:', error);
        }
      });
    } else if (!user && lastOneSignalUserId.current) {
      lastOneSignalUserId.current = null;
      window.OneSignalDeferred?.push(async (OneSignal) => {
        try {
          console.log('[OneSignal] Logging out user');
          await OneSignal.logout();
        } catch (error) {
          console.error('[OneSignal] Logout error:', error);
        }
      });
    }
  }, [user]);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (!error && data) {
      setProfile(data as Profile);
    } else {
      // Profile doesn't exist yet, it will be created by the trigger
      setProfile(null);
    }
  };

  const fetchRoles = async (userId: string) => {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);

    if (!error && data) {
      setRoles(data.map((r) => r.role as UserRole));
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
      await fetchRoles(user.id);
    }
  };

  useEffect(() => {
    let initialLoadDone = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Mark roles as loading to prevent flash
          setRolesLoading(true);
          
          // Defer Supabase calls to avoid deadlock, and wait for roles before setting loading to false
          setTimeout(async () => {
            await Promise.all([
              fetchProfile(session.user.id),
              fetchRoles(session.user.id)
            ]);
            setRolesLoading(false);
            // Only set loading false here if initial load is done (avoids race condition)
            if (initialLoadDone) {
              setLoading(false);
            }
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          setRolesLoading(false);
          if (initialLoadDone) {
            setLoading(false);
          }
        }
      }
    );

    // Initial session check - this controls the initial loading state
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Wait for roles to be loaded BEFORE setting loading to false
        await Promise.all([
          fetchProfile(session.user.id),
          fetchRoles(session.user.id)
        ]);
      }
      initialLoadDone = true;
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string): Promise<{ error: Error | null; roles?: UserRole[] }> => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    if (error) {
      return { error: error as Error };
    }

    if (data.user) {
      // Fetch roles immediately to return them
      const { data: rolesData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", data.user.id);

      const userRoles = rolesData?.map((r) => r.role as UserRole) || [];
      return { error: null, roles: userRoles };
    }

    return { error: null };
  };

  const signOut = async () => {
    // Clear local state first
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    
    // Sign out with local scope to ensure tokens are cleared from localStorage
    // This prevents the session from being restored on refresh
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.error('Sign out error:', error);
      // Even if signOut fails, we've already cleared local state
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error("Not authenticated") };

    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", user.id);

    if (!error) {
      setProfile((prev) => prev ? { ...prev, ...updates } : null);
    }

    return { error: error as Error | null };
  };

  const isAgent = roles.includes("agent") || roles.includes("admin");
  const isAdmin = roles.includes("admin");
  
  // Effective loading includes roles loading to prevent flash
  const effectiveLoading = loading || rolesLoading;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        roles,
        loading: effectiveLoading,
        isAgent,
        isAdmin,
        signIn,
        signOut,
        updateProfile,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
